<script lang="ts" setup>
import { ref, reactive, watch, computed } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { SCHEDULE_LABELS, MAINTENANCE_KEY } from '~/constants'
import BaseInput from '~/components/common/base-input.vue'
import type { DropdownItem } from '~/composables/common/interface'
import searchIcon from '~/assets/images/icons/icon-search.svg'
import bankBlankIcon from '~/assets/images/icons/icon-bank-blank.png'

const props = defineProps({
  title: {
    type: String,
    default: undefined
  },
  optionItems: {
    type: Array as () => DropdownItem[],
    required: true
  },
  isShowLabel: {
    type: Boolean,
    default: false
  },
  valueSelected: {
    type: String,
    default: ''
  },
  isBankList: {
    type: Boolean,
    default: false
  },
  isSearchable: {
    type: Boolean,
    default: false
  }
})
const labelMantainance = ref(MAINTENANCE_KEY)
const selectRef = ref()
const showOptions = ref(false)
const searchText = ref('')
const selectedItem = reactive<DropdownItem>({
  ...(props.optionItems[0] ?? {
    label: 'Vui lòng chọn 1 mục',
    value: '',
    icon: ''
  })
})

watch(
  () => props.valueSelected,
  value => {
    const valueSelected = props.optionItems.find(i => i.value === value)
    selectedItem.label = valueSelected?.label
    selectedItem.value = valueSelected?.value
    selectedItem.icon = valueSelected?.icon
    selectedItem.image = valueSelected?.image
  },
  { immediate: true, deep: true }
)

watch(
  () => props.optionItems,
  value => {
    selectedItem.label = value[0]?.label
    selectedItem.value = value[0]?.value
    selectedItem.icon = value[0]?.icon
    selectedItem.image = value[0]?.image
  },
  { deep: true }
)

watch(
  () => showOptions.value,
  () => {
    searchText.value = ''
  }
)

const itemDropdowns = computed(() => {
  return props.optionItems
    .filter(item => {
      if (!props.isShowLabel) return true
      return item !== props.optionItems[0]
    })
    .filter(item => {
      if (!props.isSearchable || !searchText.value) return true
      return item?.label?.toLowerCase().includes(searchText.value.toLowerCase())
    })
})

const emits = defineEmits(['handleItemSelected'])

onClickOutside(selectRef, () => {
  showOptions.value = false
})
function toggleDropdown(): void {
  showOptions.value = !showOptions.value
}
const selectItem = (item: DropdownItem): void => {
  showOptions.value = false
  selectedItem.label = item.label
  selectedItem.value = item.value
  selectedItem.image = item?.image
  selectedItem.icon = item?.icon
  selectedItem.isDisabled = item?.isDisabled
  selectedItem.labelStatement = item?.labelStatement
  emits('handleItemSelected', item)
}
function onChangeInput(event: string): void {
  searchText.value = event
}
</script>
<template>
  <div ref="selectRef" :class="['base-dropdown', { active: showOptions }]">
    <span v-if="title" class="base-dropdown__title">{{ title }}</span>
    <div
      :class="[
        'base-dropdown__header arrow-drop-down',
        { 'is-disabled': selectedItem?.isDisabled || selectedItem?.labelStatement === labelMantainance }
      ]"
      @click="toggleDropdown()"
    >
      <div class="selected-dropdown-item d-flex align-items-center justify-content-between">
        <div class="d-flex gap-2">
          <img
            v-if="selectedItem.image"
            :class="[selectedItem.value, { 'bank-background': isBankList }]"
            :src="selectedItem.image"
            :alt="selectedItem.label"
            width="24"
            height="24"
          />
          <i
            v-if="selectedItem?.icon"
            class="base-dropdown__icon"
            style="height: 24px; width: 24px; line-height: 24px; font-size: 24px"
            :class="selectedItem?.icon"
          ></i>
          <span
            :class="[
              'text-truncate base-dropdown__header--label',
              { 'is-select-label': isShowLabel && !selectedItem?.value }
            ]"
          >
            {{ selectedItem?.label }}
          </span>
        </div>
        <div
          v-if="selectedItem?.isDisabled && selectedItem?.labelStatement !== labelMantainance"
          class="dropdown-item__disabled-text"
        >
          Bảo trì
        </div>
      </div>
      <div v-if="selectedItem?.labelStatement" :class="['dropdown-item__disabled-text', selectedItem?.labelStatement]">
        {{ SCHEDULE_LABELS[selectedItem.labelStatement] }}
      </div>
    </div>
    <ul class="base-dropdown__content">
      <BaseInput
        v-if="isSearchable"
        :inputValue="searchText"
        type="text"
        class="input-search"
        name="inputSearch"
        placeholder="Tìm kiếm..."
        :iconPrepend="searchIcon"
        @handleKeyup="onChangeInput($event)"
        @onClearInput="searchText = ''"
      />
      <template v-if="itemDropdowns.length > 0">
        <li
          v-for="item in itemDropdowns"
          :key="item?.value"
          :class="[
            item?.key,
            'dropdown-item',
            { active: item?.label === selectedItem?.label },
            { 'is-disabled': item?.isDisabled || item?.labelStatement === labelMantainance }
          ]"
          @click="selectItem(item)"
        >
          <div class="d-flex gap-2 align-items-center">
            <img
              v-if="item.image"
              :class="[{ 'bank-background': isBankList }]"
              :src="item.image"
              :alt="item.label"
              width="24"
              height="24"
            />

            <i v-if="item?.icon" :class="item.icon"></i>
            <p class="dropdown-item__name">{{ item?.label }}</p>
          </div>
          <div
            v-if="item?.isDisabled && item?.labelStatement !== labelMantainance"
            class="dropdown-item__disabled-text"
          >
            Bảo trì
          </div>
          <div v-if="item?.labelStatement" :class="['dropdown-item__disabled-text', item?.labelStatement]">
            {{ SCHEDULE_LABELS[item?.labelStatement] }}
          </div>
        </li>
      </template>
      <template v-else>
        <div class="no-result">
          <img :src="bankBlankIcon" alt="bank blank" width="80" height="90" />
          <span class="no-result__text">Không tìm thấy kết quả phù hợp</span>
        </div>
      </template>
      <slot name="content-last"></slot>
    </ul>
  </div>
</template>
<style scoped lang="scss">
@import '~scssAsset/components/common/base-dropdown.scss';
</style>
